import { apiWithPrefix as api } from '../utils/api';

/**
 * Dashboard Service
 * Handles dashboard statistics and analytics
 */
export class DashboardService {
  /**
   * Get dashboard statistics
   */
  static async getStats() {
    try {
      const response = await api.get('/dashboard/stats');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch dashboard stats');
    }
  }

  /**
   * Get management dashboard data
   */
  static async getManagementStats() {
    try {
      const [employeesRes, attendanceRes, leaveRes] = await Promise.all([
        api.get('/employees').catch(() => ({ data: [] })),
        api.get('/management-dashboard/attendance-stats').catch(() => ({ data: {} })),
        api.get('/leave-requests').catch(() => ({ data: [] }))
      ]);

      return {
        employees: employeesRes.data,
        attendanceStats: attendanceRes.data,
        leaveRequests: leaveRes.data
      };
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch management dashboard data');
    }
  }

  static async getManagementOverview(timeRange = 'month') {
    const response = await api.get('/management-dashboard/stats', {
      params: { timeRange }
    });
    return response.data;
  }

  static async getManagementAttendanceStats(timeRange = 'month') {
    const response = await api.get('/management-dashboard/attendance-stats', {
      params: { timeRange }
    });
    return response.data;
  }

  static async getManagementRecentLeaves(limit = 5) {
    const response = await api.get('/management-dashboard/recent-leaves', {
      params: { limit }
    });
    return response.data;
  }

  /**
   * Get attendance analytics
   */
  static async getAttendanceAnalytics(period = 'month') {
    try {
      const response = await api.get('/dashboard/attendance-analytics', {
        params: { period }
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch attendance analytics');
    }
  }

  /**
   * Get leave analytics
   */
  static async getLeaveAnalytics(period = 'month') {
    try {
      const response = await api.get('/dashboard/leave-analytics', {
        params: { period }
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch leave analytics');
    }
  }

  /**
   * Get team performance metrics
   */
  static async getTeamPerformance() {
    try {
      const response = await api.get('/dashboard/team-performance');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch team performance');
    }
  }

  /**
   * Get recent activities
   */
  static async getRecentActivities(limit = 10) {
    try {
      const response = await api.get('/dashboard/recent-activities', {
        params: { limit }
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch recent activities');
    }
  }

  /**
   * Get quick stats for widgets
   */
  static async getQuickStats() {
    try {
      const response = await api.get('/dashboard/quick-stats');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch quick stats');
    }
  }

  /**
   * Get notifications summary
   */
  static async getNotificationsSummary() {
    try {
      const response = await api.get('/dashboard/notifications-summary');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch notifications summary');
    }
  }
}

export default DashboardService;
